import { POSTHOG_FEATURE_FLAGS, useFeatureFlagsReady, usePostHogFeatureFlag } from '../usePostHogFeatureFlag'
import { usePermissions } from '../usePermissions'

/**
 * Shared FGA gating for the `vetting_events` domain.
 *
 * While `internal_fga_orgv_vetting_events` is off the backend skips its check, so every
 * check here falls open — the same window handling as `AccessRoute` and `resolveNavAccess`.
 *
 * `canCreate` sits a rung above `canRead` because recording an event is not bookkeeping:
 * a pass or fail resolves the organisation's pending events for that scope and updates its
 * monitoring subscriptions, which emails the organisation and allocates a redemption code.
 *
 * Every check waits for `useFeatureFlagsReady()`. `usePostHogFeatureFlag` reads an
 * unresolved flag as `false`, which would fall open and render controls that then vanish
 * once the flag lands as on — the compliance tab has no `AccessRoute` in front of it.
 */
export function useVettingEventsAccess(): {
  canRead: boolean
  canCreate: boolean
} {
  const { userHasPermissions } = usePermissions()
  const flagsReady = useFeatureFlagsReady()
  const internalFgaOrgvVettingEventsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_VETTING_EVENTS
  )

  const isAllowed = (permission: string) =>
    flagsReady && (!internalFgaOrgvVettingEventsFeatureFlag || userHasPermissions(permission))

  return {
    canRead: isAllowed('vetting_events:can_read'),
    canCreate: isAllowed('vetting_events:can_create')
  }
}
